import React, { useState, useRef } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, FileText, Loader2, Sparkles, Download, Plus, X } from "lucide-react";

const rid = () => `r_${Math.random().toString(36).slice(2, 9)}`;
const emptyRow = () => ({ _rid: rid(), part_code: "", description: "", long_text: "" });

export default function BulkDecode() {
  const [rows, setRows] = useState([emptyRow()]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const addRow = () => setRows([...rows, emptyRow()]);
  const removeRow = (i) => { const r = [...rows]; r.splice(i, 1); setRows(r.length ? r : [emptyRow()]); };
  const setRow = (i, patch) => { const r = [...rows]; r[i] = { ...r[i], ...patch }; setRows(r); };

  // Simple CSV parser that supports quoted fields with newlines (needed for Material Long Text)
  const parseCsv = (text) => {
    const clean = text.replace(/\r/g, "");
    const parsed = [];
    let cur = [];
    let field = "";
    let inQuotes = false;
    for (let i = 0; i < clean.length; i++) {
      const c = clean[i];
      if (inQuotes) {
        if (c === '"' && clean[i + 1] === '"') { field += '"'; i++; }
        else if (c === '"') { inQuotes = false; }
        else { field += c; }
      } else {
        if (c === '"') inQuotes = true;
        else if (c === ",") { cur.push(field); field = ""; }
        else if (c === "\n") { cur.push(field); parsed.push(cur); cur = []; field = ""; }
        else { field += c; }
      }
    }
    if (field.length || cur.length) { cur.push(field); parsed.push(cur); }
    if (!parsed.length) return [];
    // Detect header
    const header = parsed[0].map((h) => (h || "").trim().toLowerCase());
    const findIdx = (...keys) => {
      for (const k of keys) {
        const idx = header.indexOf(k.toLowerCase());
        if (idx >= 0) return idx;
      }
      return -1;
    };
    const pcIdx = findIdx("part_code", "umc_no", "umc no");
    const dsIdx = findIdx("description", "material_description", "material description");
    const ltIdx = findIdx("long_text", "material_long_text", "material long text");
    const hasHeader = pcIdx >= 0 || dsIdx >= 0 || ltIdx >= 0;
    const dataRows = hasHeader ? parsed.slice(1) : parsed;
    const out = [];
    for (const cols of dataRows) {
      if (!cols || cols.every((x) => !String(x || "").trim())) continue;
      out.push({
        part_code: String((hasHeader ? cols[pcIdx >= 0 ? pcIdx : 0] : cols[0]) || "").trim(),
        description: String((hasHeader ? cols[dsIdx >= 0 ? dsIdx : 1] : cols[1]) || "").trim(),
        long_text: String((hasHeader ? cols[ltIdx >= 0 ? ltIdx : 2] : cols[2]) || "").trim(),
      });
    }
    return out.filter((r) => r.part_code);
  };

  const onFile = (f) => {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCsv(String(e.target.result));
      if (!parsed.length) { toast.error("No valid rows found in CSV"); return; }
      setRows(parsed);
      toast.success(`Loaded ${parsed.length} rows`);
    };
    reader.readAsText(f);
  };

  const runDecode = async () => {
    const items = rows.filter((r) => r.part_code.trim());
    if (!items.length) { toast.error("Add at least one part code"); return; }
    setLoading(true); setResults(null);
    try {
      const res = await api.post("/parts/bulk-decode", { items });
      setResults(res.data);
      toast.success(`Decoded ${res.data.ok} of ${res.data.total} — saved to catalog`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk decode failed");
    } finally { setLoading(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/export/parts-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "part_codes_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to download template"); }
  };

  return (
    <Layout
      title="Bulk Part Decode"
      subtitle="Paste UMC No + Material Description + Material Long Text. AI decodes into hose / fittings / sleeve and saves to your Part Catalog."
      actions={
        <button onClick={downloadTemplate} data-testid="download-template-btn" className="text-xs text-slate-700 border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
          <Download size={14} /> CSV template
        </button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-white hover:border-slate-400 hover:bg-gray-50 transition-colors cursor-pointer flex items-center justify-center gap-3"
            data-testid="bulk-csv-drop"
          >
            <UploadCloud className="text-slate-500" size={20} />
            <div className="text-sm text-slate-700">
              Drop a CSV file with columns <span className="font-mono">umc_no, material_description, material_long_text</span> — or click to browse
            </div>
            <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
          </div>

          <div className="bg-white border border-gray-200 rounded-md">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="font-display text-base font-semibold text-slate-900">Rows ({rows.length})</div>
              <button onClick={addRow} className="text-xs text-slate-700 border border-gray-200 rounded-md px-2.5 py-1 hover:bg-gray-50 inline-flex items-center gap-1"><Plus size={12} /> Add row</button>
            </div>
            <div className="p-4 space-y-3 max-h-[560px] overflow-y-auto">
              {rows.map((r, i) => (
                <div key={r._rid || `bulk-row-${i}`} className="border border-gray-100 rounded-md p-3 bg-gray-50/40" data-testid={`bulk-row-${i}`}>
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-3">
                      <label className="text-[10px] uppercase text-gray-500 font-mono tracking-widest">UMC No</label>
                      <input value={r.part_code} onChange={(e) => setRow(i, { part_code: e.target.value })} placeholder="e.g. 5823B0038" className="mt-1 w-full text-sm font-mono border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:ring-2 focus:ring-slate-300 focus:outline-none" />
                    </div>
                    <div className="col-span-8">
                      <label className="text-[10px] uppercase text-gray-500 font-mono tracking-widest">Material Description</label>
                      <input value={r.description} onChange={(e) => setRow(i, { description: e.target.value })} placeholder="Short description" className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:ring-2 focus:ring-slate-300 focus:outline-none" />
                    </div>
                    <button onClick={() => removeRow(i)} className="col-span-1 mt-6 text-gray-400 hover:text-red-600 justify-self-center"><X size={16} /></button>
                    <div className="col-span-12">
                      <label className="text-[10px] uppercase text-gray-500 font-mono tracking-widest">Material Long Text</label>
                      <textarea
                        value={r.long_text}
                        onChange={(e) => setRow(i, { long_text: e.target.value })}
                        rows={4}
                        placeholder={"MEDIA:...\nMOC OF HOSE:...\nREFERENCE STANDARD:...\nNOMINAL DIA:...\nOVERALL LENGTH:...\nPRESSURE RATING:...\nOUTER PROTECTION LAYER:...\nEND FITTING1-TYPE:...\nEND FITTING1-THREAD TYPE:...\nEND FITTING1-THREAD SIZE:...\nEND FITTING2-TYPE:...\n..."}
                        className="mt-1 w-full text-xs font-mono border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:ring-2 focus:ring-slate-300 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-100 px-4 py-3 flex items-center justify-end">
              <button
                onClick={runDecode}
                disabled={loading}
                data-testid="run-bulk-decode-btn"
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2 text-sm font-medium transition-colors inline-flex items-center gap-2 disabled:opacity-60"
              >
                {loading ? (<><Loader2 className="animate-spin" size={14} /> Decoding {rows.filter((r) => r.part_code).length} parts...</>) : (<><Sparkles size={14} /> Decode all & save to catalog</>)}
              </button>
            </div>
          </div>

          {results && (
            <div className="bg-white border border-gray-200 rounded-md overflow-hidden" data-testid="bulk-results">
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="font-display text-base font-semibold text-slate-900">Results — {results.ok}/{results.total} decoded</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left">
                      {["UMC / Part code", "Status", "Hose", "Hose (mm)", "Fitting A", "Fitting B", "Sleeve"].map((h) => (
                        <th key={h} className="px-3 py-2 text-[11px] uppercase text-gray-500 tracking-widest">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.results.map((r, i) => (
                      <tr key={`${r.part_code || "row"}-${i}`} className="border-b border-gray-100">
                        <td className="px-3 py-2 font-mono text-slate-900">{r.part_code || "—"}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${r.status === "ok" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : r.status === "error" ? "bg-red-50 text-red-700 border-red-200" : "bg-gray-50 text-gray-700 border-gray-200"}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-slate-700">{r.decoded?.hose_spec || "—"}</td>
                        <td className="px-3 py-2 font-mono text-slate-700 text-right">{r.decoded?.hose_length_mm || 0}</td>
                        <td className="px-3 py-2 text-slate-700">{r.decoded?.fitting_a || "—"}</td>
                        <td className="px-3 py-2 text-slate-700">{r.decoded?.fitting_b || "—"}</td>
                        <td className="px-3 py-2 text-slate-700">{r.decoded?.sleeve || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white border border-gray-200 rounded-md p-6">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="text-slate-600" size={18} />
              <div className="font-display text-base font-semibold text-slate-900">How it works</div>
            </div>
            <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
              <li>Paste rows or drop a CSV with <span className="font-mono text-xs">UMC No, Material Description, Material Long Text</span>.</li>
              <li>Click <span className="font-semibold">Decode all</span>. AI reads the Long Text (MEDIA, MOC, REFERENCE STANDARD, NOMINAL DIA, OVERALL LENGTH, END FITTING 1/2 etc.) and produces the exact procurement decode.</li>
              <li>Results upsert to your <span className="font-semibold">Part Catalog</span> — next PO with same UMC auto-matches.</li>
            </ol>
          </div>

          <div className="bg-white border border-gray-200 rounded-md p-6">
            <div className="text-xs uppercase tracking-widest font-mono text-gray-500 mb-2">Tip</div>
            <p className="text-sm text-gray-700">The Long Text should follow the <span className="font-mono">FIELD:VALUE</span> line format shown in the placeholder. AI ignores lines marked <span className="font-mono">NOT APPLICABLE</span>.</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
